import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useToast, ToastContainer } from "../../components/common/Toast";
import DeleteConfirmModal from "../../components/decks/DeleteConfirmModal";
import NewsletterForm from "../../components/admin/NewsletterForm";
import NewsletterTopicsPanel from "../../components/admin/NewsletterTopicsPanel";
import { useAuth } from "../../contexts/AuthContext";
import {
  newsletterAdminService,
  NewsletterEdition,
  NewsletterTopic,
  NewsletterWithMeta,
  NewsletterStatus,
  EditionStatus,
  CreateNewsletterInput,
} from "../../services/newsletterAdminService";

const editionBadge = (status: EditionStatus) => {
  const styles: Record<EditionStatus, string> = {
    draft: "bg-gray-600/20 text-gray-300 border-gray-600/50",
    approved: "bg-blue-600/20 text-blue-300 border-blue-600/50",
    sending: "bg-yellow-600/20 text-yellow-300 border-yellow-600/50",
    sent: "bg-green-600/20 text-green-300 border-green-600/50",
    failed: "bg-red-600/20 text-red-300 border-red-600/50",
  };
  return (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status]}`}
    >
      {status.toUpperCase()}
    </span>
  );
};

const statusBadge = (status: NewsletterStatus) => {
  const styles: Record<NewsletterStatus, string> = {
    active: "bg-green-600/20 text-green-400 border-green-600/50",
    paused: "bg-gray-600/20 text-gray-400 border-gray-600/50",
  };
  return (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status]}`}
    >
      {status.toUpperCase()}
    </span>
  );
};

const formatDateTime = (value: string | null): string => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
};

const describeCadence = (n: NewsletterWithMeta): string => {
  if (n.cadence === "custom" && n.custom_cadence_days) {
    return `Every ${n.custom_cadence_days} day${n.custom_cadence_days === 1 ? "" : "s"}`;
  }
  if (n.cadence === "weekly" || n.cadence === "biweekly") {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const day =
      n.send_day_of_week !== null ? days[n.send_day_of_week] : "unspecified day";
    return `${n.cadence === "weekly" ? "Weekly" : "Biweekly"} — ${day} at ${n.send_hour_utc}:00 UTC`;
  }
  return `Monthly at ${n.send_hour_utc}:00 UTC`;
};

const NewsletterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  const [newsletter, setNewsletter] = useState<NewsletterWithMeta | null>(null);
  const [editions, setEditions] = useState<NewsletterEdition[]>([]);
  const [topics, setTopics] = useState<NewsletterTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateNotes, setGenerateNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [nl, eds, tps] = await Promise.all([
        newsletterAdminService.get(id),
        newsletterAdminService.listEditions(id),
        newsletterAdminService.listTopics(id),
      ]);
      setNewsletter(nl);
      setEditions(eds);
      setTopics(tps);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load newsletter");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const topicTitleByEditionId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const t of topics) {
      if (t.used_by_edition_id) map.set(t.used_by_edition_id, t.title);
    }
    return map;
  }, [topics]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = async (input: CreateNewsletterInput) => {
    if (!id) return;
    try {
      setSubmitting(true);
      const updated = await newsletterAdminService.update(id, input);
      setNewsletter((prev) => (prev ? { ...prev, ...updated } : prev));
      showSuccess("Newsletter updated");
      setShowEditForm(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to update newsletter");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!newsletter) return;
    const next: NewsletterStatus =
      newsletter.status === "active" ? "paused" : "active";
    try {
      setTogglingStatus(true);
      const updated = await newsletterAdminService.setStatus(newsletter.id, next);
      setNewsletter((prev) => (prev ? { ...prev, ...updated } : prev));
      showSuccess(next === "active" ? "Newsletter resumed" : "Newsletter paused");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!newsletter) return;
    try {
      setDeleting(true);
      await newsletterAdminService.remove(newsletter.id);
      showSuccess("Newsletter deleted");
      navigate("/admin/newsletters");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to delete newsletter");
      setDeleting(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!newsletter) return;
    if (!promptDraft.trim()) {
      showError("Runtime prompt cannot be empty");
      return;
    }
    try {
      setSavingPrompt(true);
      const updated = await newsletterAdminService.updateRuntimePrompt(
        newsletter.id,
        promptDraft
      );
      setNewsletter((prev) => (prev ? { ...prev, ...updated } : prev));
      setEditingPrompt(false);
      showSuccess("Runtime prompt updated");
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to update runtime prompt"
      );
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleRetryPlan = async () => {
    if (!newsletter) return;
    try {
      setSavingPrompt(true);
      await newsletterAdminService.regenerateTopicPlan(newsletter.id);
      await load();
      showSuccess("Topic plan generated");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to plan topics");
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleSendRealisticTest = async () => {
    if (!newsletter) return;
    try {
      setSendingTest(true);
      const result = await newsletterAdminService.sendRealisticTest(newsletter.id);
      if (result.success) {
        showSuccess(
          "Realistic test sent",
          `Topic “${result.topicTitle}” → ${user?.email ?? "your inbox"}`
        );
      } else {
        showError(
          "Test send failed",
          result.error ?? "Mailgun returned an error"
        );
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  const handleGenerateDraft = async () => {
    if (!id) return;
    try {
      setGenerating(true);
      const edition = await newsletterAdminService.createEdition(
        id,
        generateNotes.trim() || undefined
      );
      showSuccess("Draft generated");
      setShowGenerate(false);
      setGenerateNotes("");
      navigate(`/admin/newsletters/${id}/editions/${edition.id}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to generate draft");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-6">
          <p className="text-red-300">{error ?? "Newsletter not found"}</p>
          <button
            onClick={() => navigate("/admin/newsletters")}
            className="mt-4 px-4 py-2 text-purple-300 hover:text-purple-200"
          >
            ← Back to newsletters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <button
        onClick={() => navigate("/admin/newsletters")}
        className="text-sm text-gray-400 hover:text-white mb-4"
      >
        ← Back to newsletters
      </button>

      <PageHeader title={newsletter.name} subtitle={describeCadence(newsletter)} />

      {/* Newsletter metadata card */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 mb-8">
        <div className="flex flex-wrap items-center gap-6 mb-4">
          <div>
            <div className="text-xs text-gray-500 uppercase">Status</div>
            <div className="mt-1">{statusBadge(newsletter.status)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Next run</div>
            <div className="mt-1 text-gray-300 text-sm">
              {formatDateTime(newsletter.next_run_at)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Last sent</div>
            <div className="mt-1 text-gray-300 text-sm">
              {formatDateTime(newsletter.last_sent_at)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Editions</div>
            <div className="mt-1 text-gray-300 text-sm">{newsletter.edition_count}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Topics queued</div>
            <div className="mt-1 text-gray-300 text-sm">
              {newsletter.pending_topic_count} pending · {newsletter.used_topic_count} used
            </div>
          </div>
        </div>

        {newsletter.description && (
          <p className="text-gray-400 text-sm mt-4 mb-4">{newsletter.description}</p>
        )}

        {!newsletter.runtime_system_prompt && (
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 mt-4 mb-4">
            <p className="text-amber-200 text-sm font-semibold mb-1">
              Topic plan failed
            </p>
            <p className="text-amber-100/80 text-xs mb-3">
              The newsletter was created, but topic planning didn&apos;t complete.
              Generate the plan now to start auto-sending on the cadence.
            </p>
            <button
              onClick={handleRetryPlan}
              disabled={savingPrompt}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              {savingPrompt ? "Planning..." : "Plan topics now"}
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-700">
          <button
            onClick={handleSendRealisticTest}
            disabled={
              sendingTest ||
              newsletter.pending_topic_count === 0 ||
              !newsletter.runtime_system_prompt
            }
            title={
              newsletter.pending_topic_count === 0
                ? "Refill or regenerate the topic plan first"
                : !newsletter.runtime_system_prompt
                  ? "Topic plan hasn't been generated yet"
                  : `Generate the next-up topic and email it to ${user?.email ?? "your inbox"} (topic stays in queue)`
            }
            className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 transition-all"
          >
            {sendingTest ? "Generating & sending..." : "Send realistic test to me"}
          </button>
          <button
            onClick={() => setShowGenerate(true)}
            className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all"
          >
            + Generate new draft
          </button>
          <button
            onClick={() => setShowEditForm(true)}
            className="px-4 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 transition-all"
          >
            Edit
          </button>
          <button
            onClick={handleToggleStatus}
            disabled={togglingStatus}
            className="px-4 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 transition-all disabled:opacity-50"
          >
            {newsletter.status === "active" ? "Pause" : "Resume"}
          </button>
          <button
            onClick={() => setShowDelete(true)}
            className="px-4 py-2 bg-red-600/20 text-red-400 border border-red-600/50 rounded-lg text-sm font-semibold hover:bg-red-600/30 transition-all"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Runtime prompt panel */}
      <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-gray-700 rounded-lg p-5 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-bold text-white">Runtime system prompt</h2>
            <p className="text-xs text-gray-400 mt-1">
              The distilled prompt that&apos;s used at edition-generation time. Last
              planned: {formatDateTime(newsletter.topics_last_planned_at)}
            </p>
          </div>
          {!editingPrompt && newsletter.runtime_system_prompt && (
            <button
              onClick={() => {
                setPromptDraft(newsletter.runtime_system_prompt ?? "");
                setEditingPrompt(true);
              }}
              className="px-3 py-1.5 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm hover:bg-gray-600/30"
            >
              Edit
            </button>
          )}
        </div>
        {editingPrompt ? (
          <div>
            <textarea
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              rows={10}
              disabled={savingPrompt}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
            />
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleSavePrompt}
                disabled={savingPrompt || !promptDraft.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
              >
                {savingPrompt ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setEditingPrompt(false)}
                disabled={savingPrompt}
                className="px-4 py-2 text-gray-300 hover:text-white text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <pre className="whitespace-pre-wrap text-gray-300 text-sm bg-gray-900/40 border border-gray-700 rounded-lg p-3 max-h-72 overflow-y-auto font-mono">
            {newsletter.runtime_system_prompt ??
              "— Not yet planned. Click 'Plan topics now' above to generate. —"}
          </pre>
        )}
      </div>

      {/* Topics queue panel */}
      <div className="mb-8">
        <NewsletterTopicsPanel newsletter={newsletter} onChange={load} />
      </div>

      {/* Editions table */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Editions</h2>
        {editions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-2">No editions yet</p>
            <p className="text-gray-500 text-sm">
              Generate your first draft to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    #
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Topic
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Subject
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Generated
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Sent
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Recipients
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {editions.map((ed) => (
                  <tr
                    key={ed.id}
                    className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="py-4 px-4 text-white text-sm">
                      #{ed.edition_number}
                    </td>
                    <td className="py-4 px-4">{editionBadge(ed.status)}</td>
                    <td className="py-4 px-4 text-gray-300 text-sm max-w-xs truncate">
                      {topicTitleByEditionId.get(ed.id) ?? (
                        <span className="text-gray-500 italic">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-gray-300 text-sm max-w-md truncate">
                      {ed.subject ?? (
                        <span className="text-gray-500 italic">— not yet generated —</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-gray-300 text-sm">
                      {formatDateTime(ed.generated_at)}
                    </td>
                    <td className="py-4 px-4 text-gray-300 text-sm">
                      {formatDateTime(ed.sent_at)}
                    </td>
                    <td className="py-4 px-4 text-gray-300 text-sm">
                      {ed.recipient_count ?? "—"}
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() =>
                          navigate(`/admin/newsletters/${newsletter.id}/editions/${ed.id}`)
                        }
                        className="px-3 py-1.5 bg-purple-600/20 text-purple-300 border border-purple-600/50 rounded-md text-xs font-semibold hover:bg-purple-600/30 transition-all"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditForm && (
        <NewsletterForm
          initial={newsletter}
          submitting={submitting}
          onCancel={() => setShowEditForm(false)}
          onSubmit={handleEdit}
        />
      )}

      {showDelete && (
        <DeleteConfirmModal
          title="Delete newsletter?"
          message={`This permanently deletes "${newsletter.name}" and all of its editions. This cannot be undone.`}
          confirmLabel="Delete"
          isDeleting={deleting}
          onCancel={() => (deleting ? undefined : setShowDelete(false))}
          onConfirm={handleDelete}
        />
      )}

      {showGenerate && (
        <GenerateDraftModal
          notes={generateNotes}
          onNotesChange={setGenerateNotes}
          submitting={generating}
          onCancel={() => {
            if (generating) return;
            setShowGenerate(false);
            setGenerateNotes("");
          }}
          onSubmit={handleGenerateDraft}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

interface GenerateDraftModalProps {
  notes: string;
  onNotesChange: (value: string) => void;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const GenerateDraftModal: React.FC<GenerateDraftModalProps> = ({
  notes,
  onNotesChange,
  submitting,
  onCancel,
  onSubmit,
}) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg max-w-lg w-full p-6">
        <h2 className="text-lg font-bold text-white mb-3">Generate new draft</h2>
        <p className="text-sm text-gray-400 mb-4">
          Optional: add talking points or context for this edition.
        </p>
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          disabled={submitting}
          rows={6}
          placeholder="e.g. Highlight the new integrations shipped this week."
          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 mb-4"
        />
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-5 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 transition-all"
          >
            {submitting ? "Generating..." : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterDetailPage;
